import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Text,
  PersonIcon,
  CourierIcon,
  CheckIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
} from '@citibox-panel/ui';
import { USER_COURIER_LABEL } from '../../../lib/userCourierLabels';

export type ActionAudience = 'user' | 'courier';

export interface AudienceSelectProps {
  value: ActionAudience;
  onChange: (audience: ActionAudience) => void;
  // The write in flight cannot be cancelled, so the list must not change under the agent.
  disabled?: boolean;
}

// The words come from the app's shared map, so this selector and the ficha's account-type strip can
// never name the two sides differently. The glyphs stay here: they belong to this control.
const AUDIENCES: Record<ActionAudience, { label: string; Icon: typeof PersonIcon }> = {
  user: { label: USER_COURIER_LABEL.user, Icon: PersonIcon },
  courier: { label: USER_COURIER_LABEL.courier, Icon: CourierIcon },
};

// Fixes the menu's order: Object.keys over the record above comes back as bare strings.
const ORDER: readonly ActionAudience[] = ['user', 'courier'];

export function AudienceSelect({ value, onChange, disabled }: AudienceSelectProps) {
  // Menu.anchorEl is required and takes the element itself, so the popover hangs from the real
  // rendered <button> rather than the wrapper.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const active = AUDIENCES[value];

  return (
    <>
      <Button
        ref={triggerRef}
        // Outlined where its precedent is text: this trigger sits on the card header with no
        // surrounding control to lean on, so the bordered pill is its own (the design of record).
        variant="outlined"
        color="neutral"
        size="small"
        sentenceCase
        {...(disabled !== undefined ? { disabled } : {})}
        onClick={() => {
          setOpen((isOpen) => !isOpen);
        }}
        // The visible text is the current value, so the name states the purpose and still contains
        // it (WCAG 2.5.3). TODO: i18n
        aria-label={`Audiencia de las acciones: ${active.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        startIcon={<active.Icon fontSize="small" />}
        endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      >
        {active.label}
      </Button>
      <Menu
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        {ORDER.map((audience) => {
          const { label, Icon } = AUDIENCES[audience];
          const isActive = audience === value;
          return (
            <MenuItem
              key={audience}
              role="menuitemradio"
              aria-checked={isActive}
              selected={isActive}
              onClick={() => {
                onChange(audience);
                setOpen(false);
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <Text color={isActive ? 'brand' : 'primary'}>{label}</Text>
              {isActive && (
                // Decorative echo of aria-checked, trailing the row as the design images show it.
                <Box sx={{ display: 'flex', ml: 'auto', pl: 2 }}>
                  <CheckIcon fontSize="small" />
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
